/**
 * Maps Altruon StyleContext to Checkout.com Flow appearance configuration
 * 
 * @param {Object} styles - Altruon styles from StyleContext
 * @returns {Object} Checkout.com Flow appearance object
 * 
 * Docs: https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/customize-your-flow-integration
 */
export const mapStylesToCheckoutFlow = (styles) => {
  if (!styles) {
    return {};
  }

  return {
    // Color customization
    colorAction: styles.components?.button?.primary?.backgroundColor || styles.colors?.primary || '#5E48FC',
    colorBackground: styles.colors?.background || styles.colors?.pageBackground || '#FFFFFF',
    colorBorder: styles.components?.input?.borderColor || '#E5E7EB',
    colorDisabled: styles.components?.button?.primary?.backgroundColorDisabled || '#9CA3AF',
    colorError: styles.components?.errorMessage?.color || '#EF4444',
    colorFormBackground: styles.components?.input?.backgroundColor || '#FFFFFF',
    colorFormBorder: styles.components?.input?.borderColor || '#E5E7EB',
    colorInverse: styles.colors?.textPrimary || '#040507ff',
    // colorOutline: styles.colors?.borderFocus || '#d3e5f8ff',
    colorPrimary: styles.colors?.textPrimary || '#020303ff',
    colorSecondary: styles.colors?.textSecondary || '#6B7280',
    colorSuccess: styles.colors?.success || '#10B981',
    
    // Font customization for different UI elements
    button: {
      fontFamily: styles.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontSize: styles.components?.button?.primary?.fontSize || '16px',
      fontWeight: parseInt(styles.components?.button?.primary?.fontWeight) || 600,
      letterSpacing: parseFloat(styles.typography?.letterSpacing) || 0,
      lineHeight: styles.typography?.lineHeight || '24px',
    },
    
    footnote: {
      fontFamily: styles.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontSize: styles.typography?.fontSizes?.sm || '14px',
      fontWeight: 400,
      letterSpacing: parseFloat(styles.typography?.letterSpacing) || 0,
      lineHeight: '20px',
    },
    
    input: {
      fontFamily: styles.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontSize: styles.components?.input?.fontSize || '16px',
      fontWeight: parseInt(styles.components?.input?.fontWeight) || 400,
      letterSpacing: parseFloat(styles.typography?.letterSpacing) || 0,
      lineHeight: '24px',
    },
    
    label: {
      fontFamily: styles.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontSize: styles.components?.label?.fontSize || '14px',
      fontWeight: parseInt(styles.components?.label?.fontWeight) || 500,
      letterSpacing: parseFloat(styles.components?.label?.letterSpacing) || 0,
      lineHeight: '20px',
    },
    
    subheading: {
      fontFamily: styles.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontSize: styles.typography?.fontSizes?.lg || '18px',
      fontWeight: parseInt(styles.typography?.fontWeights?.semibold) || 600,
      letterSpacing: parseFloat(styles.typography?.letterSpacing) || 0,
      lineHeight: '28px',
    },
    
    // Border radius
    borderRadius: [
      styles.borderRadius?.small || styles.components?.input?.borderRadius || '6px',
      styles.borderRadius?.medium || styles.components?.button?.primary?.borderRadius || '8px',
    ],
  };
};

/**
 * Create component options for Checkout.com Flow
 * 
 * @param {Object} config - Component configuration
 * @returns {Object} Checkout.com componentOptions
 */
export const createCheckoutComComponentOptions = (config = {}) => {
  return {
    flow: {
      showPayButton: false,  // Hide built-in pay button, we control submission
    },
    card: {
      // Pre-fill cardholder name if available
      data: {
        cardholderName: config.cardholderName || '',
      },
      // Position of cardholder name field: 'top', 'bottom', or 'hidden'
      displayCardholderName: config.displayCardholderName || 'top',
    },
  };
};

export default {
  mapStylesToCheckoutFlow,
  createCheckoutComComponentOptions,
};

